import React, { useState, useEffect, useCallback } from 'react';
import { commentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { MessageSquare, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Comments = ({ entityType, entityId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!entityId) return;
    try {
      const res = await commentsApi.getAll(entityType, entityId);
      setComments(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await commentsApi.create({
        entity_type: entityType,
        entity_id: entityId,
        content: newComment.trim()
      });
      setNewComment('');
      fetchComments();
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (commentId) => {
    try {
      await commentsApi.delete(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch { toast.error('Failed to delete comment'); }
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div data-testid="comments-section">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-navy/50" />
        <span className="text-sm font-semibold text-[#1A2B5E]">Comments</span>
        <Badge variant="outline" className="text-xs">{comments.length}</Badge>
      </div>

      {/* New comment form */}
      <form onSubmit={handleSubmit} className="flex gap-2 mb-4" data-testid="comment-form">
        <div className="w-8 h-8 rounded-full bg-[#106EFD] flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
          {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
        </div>
        <div className="flex-1">
          <Textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Leave a comment..."
            rows={2}
            className="text-sm resize-none"
            data-testid="comment-input"
          />
          <div className="flex justify-end mt-1.5">
            <Button
              type="submit"
              size="sm"
              disabled={!newComment.trim() || submitting}
              className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
              data-testid="comment-submit-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Post
            </Button>
          </div>
        </div>
      </form>

      {/* Comment list */}
      <div className="space-y-3">
        {loading ? (
          <p className="text-sm text-navy/40">Loading comments...</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-navy/40">No comments yet. Be the first!</p>
        ) : comments.map(comment => (
          <div key={comment.id} className="flex gap-2 group" data-testid={`comment-${comment.id}`}>
            <div className="w-8 h-8 rounded-full bg-[#1A2B5E]/10 flex items-center justify-center text-[#1A2B5E] text-xs font-medium flex-shrink-0">
              {comment.author_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#1A2B5E]">{comment.author_name}</span>
                  <span className="text-[10px] text-navy/30">{timeAgo(comment.created_at)}</span>
                </div>
                {(comment.author_id === user?.id || user?.role === 'admin') && (
                  <button
                    onClick={() => handleDelete(comment.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-red-500 text-navy/30"
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <p className="text-sm text-navy/70 mt-1 whitespace-pre-wrap">{comment.content}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Comments;
